/* A group invitation, opened from a link.
 *
 * THE LINK IS THE CREDENTIAL, so this page is public: somebody with no account
 * must be able to open it, see what they were invited to, and decide. It shows
 * the group, its church, and a MASKED version of the number the invitation was
 * sent to — enough to recognise your own, never enough to harvest one from a
 * forwarded WhatsApp message.
 *
 * THREE STATES, and the whole design is in which one somebody lands on:
 *
 *   signed in as the invited person  → one button, they are in
 *   signed in as somebody else       → say so plainly; the server refuses anyway
 *   no account                       → sign up, and the token comes with them
 *
 * The third is the one that matters. A leader pastes a link into WhatsApp and
 * the person on the other end has never heard of this app — asking them to
 * register, find the group, and ask to join is three chances to give up. The
 * token rides through signup and the join happens on the far side of the OTP,
 * so they arrive already in the group.
 */
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, Church, Users } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { api, tokens, ApiError } from '@/api/client'

type InviteLink = {
  group_id: string
  group_name: string
  church_name: string
  status: string
  invited_mobile_hint: string | null
}

export function InvitePage() {
  const { token = '' } = useParams<{ token: string }>()
  const navigate = useNavigate()

  const [invite, setInvite] = useState<InviteLink | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [joining, setJoining] = useState(false)
  const [joined, setJoined] = useState(false)

  const signedIn = Boolean(tokens.access)

  useEffect(() => {
    let cancelled = false
    api
      .get<InviteLink>(`/signup/invite/${token}`, { auth: false })
      .then((res) => {
        if (!cancelled) setInvite(res)
      })
      .catch(() => {
        if (!cancelled) setError('That invitation link is not valid.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [token])

  async function join() {
    if (joining) return
    setJoining(true)
    setError(null)
    try {
      await api.post(`/signup/invite/${token}/accept`, {})
      setJoined(true)
      // Straight into the group. Landing on a "you have joined" screen and
      // making them find it again would be a step for nothing.
      setTimeout(() => navigate(`/groups/${invite?.group_id}`), 900)
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'That did not work. Try again.',
      )
      setJoining(false)
    }
  }

  if (loading) return <Shell />

  if (!invite) {
    return (
      <Shell>
        <AlertCircle className="mx-auto h-8 w-8 text-danger" aria-hidden />
        <h1 className="mt-s3 text-xl font-bold">
          That invitation link is not valid
        </h1>
        <p className="mt-s2 text-base text-ink-2">
          It may have been used already, or withdrawn. Ask whoever invited you
          to send a new one.
        </p>
      </Shell>
    )
  }

  if (invite.status !== 'pending') {
    return (
      <Shell>
        <Users className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
        <h1 className="mt-s3 text-xl font-bold">
          This invitation has already been {invite.status}
        </h1>
        <p className="mt-s2 text-base text-ink-2">
          If that was not you, ask whoever invited you to send another.
        </p>
      </Shell>
    )
  }

  return (
    <Shell>
      <div className="grid h-[52px] w-[52px] place-items-center rounded-pill bg-brand-soft mx-auto">
        <Users className="h-6 w-6 text-brand" aria-hidden />
      </div>
      <h1 className="mt-s3 text-xl font-bold">{invite.group_name}</h1>
      <p className="mt-1 flex items-center justify-center gap-s2 text-base text-ink-2">
        <Church className="h-4 w-4" aria-hidden />
        {invite.church_name}
      </p>

      {invite.invited_mobile_hint ? (
        <p className="mt-s4 text-base text-ink-2">
          Invitation sent to{' '}
          <strong className="text-ink">{invite.invited_mobile_hint}</strong>
        </p>
      ) : null}

      {joined ? (
        <p className="mt-s5 text-md font-bold text-success-ink">
          You have joined {invite.group_name}
        </p>
      ) : signedIn ? (
        <>
          <div className="mt-s5">
            <Button variant="primary" size="lg" full loading={joining} onClick={join}>
              Join {invite.group_name}
            </Button>
          </div>
          {/* The server matches on the mobile this account VERIFIED, so
              somebody who followed a link meant for a neighbour is refused
              rather than quietly queued. Said here so the refusal is not a
              surprise. */}
          <p className="mt-s3 text-sm text-ink-2">
            You can only accept this from the account with that number.{' '}
            <button
              type="button"
              onClick={() => {
                tokens.clear()
                window.location.reload()
              }}
              className="font-semibold text-brand hover:underline"
            >
              Use a different account
            </button>
          </p>
        </>
      ) : (
        <>
          <div className="mt-s5">
            <Button
              variant="primary"
              size="lg"
              full
              onClick={() => navigate(`/signup?invite=${token}`)}
            >
              Sign up to join
            </Button>
          </div>
          <p className="mt-s3 text-base text-ink-2">
            Already have an account?{' '}
            <Link
              to={`/signin?next=${encodeURIComponent(`/invite/${token}`)}`}
              className="font-semibold text-brand hover:underline"
            >
              Sign in
            </Link>
          </p>
        </>
      )}

      {error ? (
        <p className="mt-s4 rounded bg-danger-soft p-s3 text-base text-danger">
          {error}
        </p>
      ) : null}
    </Shell>
  )
}

function Shell({ children }: { children?: React.ReactNode }) {
  return (
    <div className="grid min-h-screen grid-cols-[minmax(0,1fr)] place-items-center bg-canvas p-s5">
      <div className="w-[460px] max-w-full rounded-md bg-surface p-s6 text-center shadow-2">
        {children ?? (
          <p className="py-s6 text-base text-ink-2">Loading…</p>
        )}
      </div>
    </div>
  )
}
